export type GDFPoint = [number, number];

export interface GDFHeader {
    version: number;
    productId: number;
    latNorth: number;
    latSouth: number;
    lonEast: number;
    lonWest: number;
    maxPoints: number;
    productType: GDFProductType;
    productPallet: GDFPalletCode;
}

export interface GDFFrameDefinitionRecord {
    sequence: number;
    offset: number;
}

export interface GDFRecordHeader {
    type: GDFRecordType;
    length: number;
}

export interface GDFFrame {
    header: GDFHeader;
    records: GDFRecord[];
}

export type GDFKnownRecord =
    | GDFHotPolyHiRecord
    | GDFHotPolyRecord
    | GDFDateTime2Record
    | GDFDateTimeRecord
    | GDFNavInfo2Record
    | GDFRle1Record
    | GDFNavInfoRecord
    | GDFWmoCloudRecord
    | GDFWmoHzdVolRecord
    | GDFLegendRecord
    | GDFTitleRecord
    | GDFWindsRecord
    | GDFWindsCompactRecord
    | GDFWindsSmallRecord
    | GDFMetarRecord
    | GDFTafRecord
    | GDFDelayRecord
    | GDFCcfpRecord
    | GDFRvrRecord
    | GDFDiversionRecord
    | GDFLabelRecord
    | GDFPirepRecord
    | GDFPirep2Record
    | GDFFcaRecord
    | GDFRadarSummaryRecord
    | GDFCustomRecord
    | GDFIataTurbRecord
    | GDFWmoVolcanoRecord
    | GDFWmoVolcano2Record
    | GDFPolyRecord
    | GDFLightningRecord
    | GDFWmoStormRecord
    | GDFWmoTropRecord
    | GDFWmoCatRecord
    | GDFWmoCloudRecord
    | GDFWmoJetStreamRecord
    | GDFWmoRadiationRecord
    | GDFFrontRecord
    | GDFTapsRecord;

export type GDFRecord = GDFKnownRecord | GDFUnknownRecord;
export type GDFSubRecord = GDFWmoVolcanoAshCloud | GDFWmoVolcano2CloudRecord;

export interface GDFUnknownRecord {
    type: Exclude<GDFRecordType, GDFKnownRecord['type']>;
}

export type GDFHotPolyHiRecord = GDFHotPolyHiRecordNormal | GDFHotPolyHiRecordReduced;
export type GDFHotPolyRecord = GDFHotPolyRecordNormal | GDFHotPolyRecordReduced;

export interface GDFHotPolyHiRecordNormal {
    type: GDFRecordType.hotpolyhi;
    reduced: false;
    flags: number;
    shape: GDFHotPollyShape;
    polyType: GDFHotPollyTypes;
    startTime: number | null;
    endTime: number | null;
    id: number | null;
    tops: number | null;
    speed: number | null;
    dir: number | null;
    issuedTime: number | null;
    subType: number;
    severity: number;
    base: number | null;
    coverage: number;
    outlook: number;
    suaBase: number | null;
    suaTop: number | null;
    probSevere: number | null;
    probHail: number | null;
    probWind: number | null;
    probTornado: number | null;
    probLightning: number | null;
    speedDirBasePoint: GDFPoint | null;
    points: GDFPoint[];
    label: string;
    detail: string;
}

export interface GDFHotPolyHiRecordReduced {
    type: GDFRecordType.hotpolyhi;
    reduced: true;
    polyType: GDFHotPollyTypes;
    points: Float32Array;
    bbox: Float32Array;
    severity: number;
}

export interface GDFHotPolyRecordNormal {
    type: GDFRecordType.hotpoly;
    reduced: false;
    flags: number;
    shape: GDFHotPollyShape;
    polyType: GDFHotPollyTypes;
    startTime: number | null;
    endTime: number | null;
    id: number;
    tops: number;
    speed: number;
    dir: number;
    label: string;
    detail: string;
    points: GDFPoint[];
}

export interface GDFHotPolyRecordReduced {
    type: GDFRecordType.hotpoly;
    reduced: true;
    polyType: GDFHotPollyTypes;
    points: Float32Array;
    bbox: Float32Array;
    severity: number;
}

export interface GDFDateTime2Record {
    type: GDFRecordType.datetime2;
    validFrom: number | null;
    validTo: number | null;
    createdAt: number | null;
}

export interface GDFDateTimeRecord {
    type: GDFRecordType.datetime;
    time: number | null;
}

export interface GDFRle1Record {
    type: GDFRecordType.rle1;
    line: number;
    data: ArrayBufferLike;
}

export interface GDFNavInfo2Record {
    type: GDFRecordType.navinfo2;
    width: number;
    height: number;
    lonLeft: number;
    lonRight: number;
    latTop: number;
    latBottom: number;
}

export interface GDFNavInfoRecord {
    type: GDFRecordType.navinfo;
    lines: number;
    elements: number;
    centerLon: number;
    centerLat: number;
    latTop: number;
    degreesPerLine: number;
    degreesPerElement: number;
}

export interface GDFWmoTropRecord {
    type: GDFRecordType.wmotrop;
    point: [number, number];
    subType: GDFWmoTropType;
    height: number | null;
}

export interface GDFWmoCloudRecord {
    type: GDFRecordType.wmocloud;
    points: GDFPoint[];
    label: string;
    cloudBase: number | null;
    cloudTops: number | null;
    cloudType: number;
    dist: number;
    turbLevel: number;
    turbBase: number;
    turbTops: number;
    iceLevel: number;
    iceBase: number;
    iceTops: number;
    shape: number;
    bbox: number[];
}

export interface GDFLegendRecord {
    type: GDFRecordType.legend;
    text: string;
}
export interface GDFTitleRecord {
    type: GDFRecordType.title;
    text: string;
}

export interface GDFWmoVolcanoAshCloud {
    type: GDFSubRecordType.wmoAshCloud;
    interval: number;
    dir: number;
    speed: number;
    base: number | null;
    tops: number | null;
    points: GDFPoint[];
}

export interface GDFWmoVolcanoRecord {
    type: GDFRecordType.wmovolcano;
    point: GDFPoint;
    name: string;
    id: string;
    summit: number | null;
    color: number;
    reportTime: number | null;
    nextReportTime: number | null;
    advisory: string;
    details: string;
    properties: string;
    clouds: GDFWmoVolcanoAshCloud[];
}

export interface GDFCustomRecord {
    type: GDFRecordType.custom;
    shape: GDFCustomDrawShape;
    usePen: boolean;
    penWidth: number;
    penStyle: GDFCustomDrawPenStyle;
    penColor: string;
    useFill: boolean;
    fillStyle: GDFCustomDrawFillStyle;
    fillColor: string;
    headCap: GDFCustomDrawCap;
    tailCap: GDFCustomDrawCap;
    useHotPoint: boolean;
    ellipseType: GDFCustomDrawEllipseType;
    range: number;
    menu: string;
    menuItem: string;
    tipText: string[];
    point: GDFPoint;
    points: GDFPoint[];
    properties: string;
}

export interface GDFWindsRecord {
    type: GDFRecordType.winds;
    point: GDFPoint;
    direction: number; // degrees, 0-360
    speed: number; // knots
    useColorSpeed: number; // flag to use custom color
    colorSpeed: number; // ARGB color
    tropopause: number; // 100s ft
    useColorTrop: number; // flag to use custom color
    colorTrop: number; // ARGB color
    temperature: number; // celcius
    userColorTemp: number; // flag to use custom color
    colorTemp: number; // ARGB color
    altitude: number; // 100s ft
    useColorAlt: number; // flag to use custom color
    colorAlt: number; // ARGB color
}

export interface GDFWindsCompactRecord {
    type: GDFRecordType.windscompact;
    altitude: number; // 100s ft
    topLeft: GDFPoint; // top-left corner of the grid
    latResolution: number; // latitude resolution in degrees
    lonResolution: number; // longitude resolution in degrees
    gridWidth: number; // number of points in the grid in longitude direction
    gridHeight: number; // number of points in the grid in latitude direction
    dataType: 'wind' | 'reflectivity'; // type of the data in the grid (currently only 'wind' is supported)
    data: GDFWindsCompactData;
}

export interface GDFWindsCompactData {
    length: number; // number of records in the data
    temperature: Int8Array; // Temperature [-90, 60] deg C
    direction: Int16Array; // Wind direction [0, 359) degrees
    speed: Uint8Array; // Wind speed [0, 248.81] kt (128 m/s * MS_TO_KT)
}

export interface GDFWindsSmallRecord {
    type: GDFRecordType.windssm;
    point: GDFPoint;
    direction: number;
    speed: number;
    temperature: number;
    altitude: number;
}

export interface GDFPolyRecord {
    type: GDFRecordType.poly;
    shape: GDFHotPollyShape;
    points: GDFPoint[];
}

export interface GDFMetarRecord {
    type: GDFRecordType.metar;
    id: string; // Weather station ID, space padded; NO NULL
    class: number; // Class of airport for display purposes
    point: GDFPoint;
    isDecoded: boolean; // 0=Not decoded, 1=Decoded (if not decoded, all fields below will be NULL)
    isSpecial: boolean | null; // 0=NULL, 1=Not Speci, 2=Speci
    subType: 'A' | 'M' | 'C' | 'N'; // A=Auto generated, M=Manually generated, C=Corrected, N=Nil reported (NULL)
    date: number | null; // Date/time of the report (NWS time stamp; UTC) (NULL = year = 0)
    flightCategory: GDFMetarTafFlightCategory; // 0=Unknown, 1=VFR, 2=MVFR, 3=IFR, 4=LIFR, 5=BCAT1
    windDir: number | null; // Degrees (65534 = variable, 65535 = NULL)
    windVarStart: number | null; // Degrees (65535 = NULL)
    windVarStop: number | null; // Degrees (65535 = NULL)
    windSpeed: number | null; // Knots (255 = NULL)
    windGusts: number | null; // Knots (255 = NULL)
    visLessGrt: 'G' | 'L' | null; // G=greater than, L=less than (255 = NULL)
    visibility: number | null; // Statute miles * 100 (65535 = NULL)
    visHeading: number | null; // Degrees (65535 = NULL)
    temperature: number | null; // Degrees celsius (127 = NULL)
    dewPoint: number | null; // Degrees celsius (127 = NULL)
    altimeter: number | null; // Inches * 100 (65535 = NULL)
    ceiling: number | null; // Feet / 100 (65534 = >12k, 65535 = NULL)
    skyCoverage: number | null; // 0=missing, 1=CLR, 2=SKC, 3=FEW, 4=SCT, 5=BKN, 6=OVC, 7=OVX (METAR repts vert vis but no cloud info)
    text: string;
}

export interface GDFTafRecord {
    type: GDFRecordType.taf;
    id: string;
    point: GDFPoint;
    flightCategory: GDFMetarTafFlightCategory;
    windSpeed: number | null;
    storm: boolean;
    stormVicinity: boolean;
    visualLessGt: 'G' | 'L' | null;
    ceiling: number | null;
    visibility: number | null;
    text: string | null;
}

export interface GDFDelayElement {
    type: 'C' | 'P' | 'G' | 'A' | 'D';
    createTime: number;
    updateTime: number;
    startTime: number;
    stopTime: number;
    delayMinutesMinAvg: number;
    delayMinutesMax: number;
    trend: number;
    reason: string;
}

export interface GDFDelayRecord {
    type: GDFRecordType.delay;
    airportId: string;
    class: number;
    point: GDFPoint;
    elements: GDFDelayElement[];
    level: number;
    trend: number;
}

export interface GDFCcfpRecord {
    type: GDFRecordType.ccfp;
    geometryType: GDFCcfpGeometryType;
    coverage: number;
    prob: number;
    growth: number;
    tops: number;
    speed: number;
    direction: number;
    points: GDFPoint[];
}

export interface GDFRvrElement {
    runway: string;
    touchDownVisibility: number;
    midpointVisibility: number;
    rollOutVisibility: number;
    touchDownVisibilityTrend: number;
    midpointVisibilityTrend: number;
    rolloutVisibilityTrend: number;
    lightsEdge: number;
    lightsCenter: number;
}

export interface GDFRvrRecord {
    type: GDFRecordType.rvr;
    airportId: string;
    class: number;
    point: GDFPoint;
    elements: GDFRvrElement[];
    minimumVisibility: number;
}

export interface GDFDiversionElement {
    type: 'T' | 'F';
    acid: string;
    airportToFrom: string;
    point: GDFPoint;
    active: number; // Bool?
}

export interface GDFDiversionRecord {
    type: GDFRecordType.diversion;
    airportId: string;
    class: number;
    point: GDFPoint;
    fromCount: number;
    fromTrend: 'I' | 'D' | 'N' | 'U';
    toCount: number;
    toTrend: 'I' | 'D' | 'N' | 'U';
    elements: GDFDiversionElement[];
}

export interface GDFLabelRecord {
    type: GDFRecordType.label;
    horizontalAlign: number;
    verticalAlign: number;
    coordType: number;
    point: GDFPoint;
    latOffset: number;
    lonOffset: number;
    text: string;
}

export interface GDFPirep2Record {
    type: GDFRecordType.pirep2;
    time: number | null;
    id: number;
    acid: string;
    acType: string;
    pointFrom: GDFPoint;
    pointTo: GDFPoint;
    flightLevelUpper: number | null;
    flightLevelLower: number | null;
    altitudeUpper: number | null;
    altitudeLower: number | null;
    windSpeed: number | null;
    windDir: number | null;
    temp: number | null;
    skyCover: number | null;
    cloudBase: number | null;
    cloudTop: number | null;
    weather: number | null;
    visibility: number | null;
    icingSeverity: number | null;
    icingDetails: number;
    icingColor: number;
    turbulenceSeverity: number | null;
    turbulenceType: number;
    turbulenceBaseDuration: number;
    turbulenceWorseDuration: number;
    turbulenceColor: number;
    urgent: boolean;
    llws: boolean;
    report: string;
}

export interface GDFFcaRecord {
    type: GDFRecordType.fca;
    shape: GDFFcaShape;
    subType: GDFFcaType;
    fcaStartTime: number | null;
    fcaEndTime: number | null;
    afpStartTime: number | null;
    afpEndTime: number | null;
    flags: number;
    lookAheadTime: number;
    ceiling: number | null;
    floor: number | null;
    avgDelay: number | null;
    direction: number | null;
    speed: number | null;
    point: GDFPoint;
    radius: number;
    name: string;
    label: string;
    detail: string;
    filter: string;
    points: GDFPoint[];
}

export interface GDFPirepRecord {
    type: GDFRecordType.pirep;
    point: GDFPoint;
    time: number | null;
    id: string;
    altitude: number | null;
    wind: number | null;
    windDir: number | null;
    temp: number | null;
    turbIntensity: number | null;
    icingIntensity: number | null;
    text: string;
}

export interface GDFRadarSummaryRecord {
    type: GDFRecordType.radsum;
    point: GDFPoint;
    tops: number | null;
    dir: number | null;
    speed: number | null;
    hailSize: number | null;
    meso: boolean;
    tvs: boolean;
    zoom: number;
    intensity: number;
}

export interface GDFIataTurbRecord {
    type: GDFRecordType.iataturb;
    point: GDFPoint;
    reportTime: number | null;
    altitude: number | null; // 1s of feet from -1000 to 90,000 +1000 (to shift up from negative numbers) (0x1FFFF=NULL)
    meanEdr: number | null; // 0.000-1.000 * 1000 (0-1000, 0x3FF=NULL)
    meanConf: number | null; // 0.0-1.0 * 10 (0-10, 0xF=NULL)
    windSpeed: number | null; // Sustained winds in knots (0-254, 0xFF=unknown)
    windDir: number | null; // 0 to 360 degrees  (0x1FF=unknown)
    peakEdr: number | null; // 0.000-1.000 * 1000 (0-1000, 0x3FF=NULL)
    peakConf: number | null; // 0.0-1.0 * 10 (0-10, 0xF=NULL)
    temp: number | null; // Outside temperature in Celsius (-100 to +100, -127=NULL)
}

export interface GDFLightningRecord {
    type: GDFRecordType.lightning;
    // Those are already mercator projected
    points: Float32Array;
    timeDev: Int16Array;
    amps: Int8Array;
    strokes: Uint8Array;
    count: number;
}

export interface _GDFLightningRecord {
    type: GDFRecordType.lightning;
    point: GDFPoint;
    timeDev: number;
    amps: number;
    strokes: number;
}

export interface GDFWmoStormRecord {
    type: GDFRecordType.wmostorm;
    point: GDFPoint;
    stormType: GDFWmoStormType;
    name: string;
}

export interface GDFWmoRadationRecord {
    type: GDFRecordType.wmohzdrad;
    point: GDFPoint;
    time: number | null;
}

export interface GDFWmoCatRecord {
    type: GDFRecordType.wmocat;
    severity: number;
    base: number | null;
    top: number | null;
    points: GDFPoint[];
}

export interface GDFWmoCloud {
    type: GDFRecordType.wmocloud;
    point: GDFPoint[];
}

export interface GDFWmoHzdVolRecord {
    type: GDFRecordType.wmohzdvol;
    point: GDFPoint;
    time: number | null;
    name: string;
}

export interface GDFWmoRadiationRecord {
    type: GDFRecordType.wmohzdrad;
    point: GDFPoint;
    time: number | null;
}

export interface GDFWmoJetStreamPoint {
    point: GDFPoint;
    height: number | null;
    speed: number | null;
    lowerDepth: number | null;
    upperDepth: number | null;
}

export interface GDFWmoJetStreamRecord {
    type: GDFRecordType.wmojetstream;
    points: GDFWmoJetStreamPoint[];
}

export interface GDFFrontRecord {
    type: GDFRecordType.front;
    frontType: GDFFrontType;
    id: number | null;
    points: GDFPoint[];
}

export interface GDFTapsPrivateRecord {
    acid: string;
    tail: string;
    // Rest is not used so don't include to save space
}

export interface GDFTapsRecord {
    type: GDFRecordType.taps;
    point: GDFPoint;
    reportTime: number | null;
    acType: string;
    turbType: GDFTapsType;
    altitude: number | null;
    windSpeed: number | null;
    windDir: number | null;
    temp: number | null;
    hazardMetric: number | null;
    hazardScaled: number | null;
    edr: number | null;
    rmsLoad: number | null;
    private: GDFTapsPrivateRecord | null;
}

export interface GDFWmoVolcano2CloudRecord {
    type: GDFSubRecordType.wmoAshCloud2;
    interval: number;
    base: number | null;
    tops: number | null;
    concentration: number | null;
    points: GDFPoint[];
}

export interface GDFWmoVolcano2Record {
    type: GDFRecordType.wmovolcano2;
    point: GDFPoint;
    name: string;
    id: string;
    summit: number | null;
    reportTime: number | null;
    nextReportTime: number | null;
    advisory: string;
    details: string;
    properties: string;
    color: number;
    operational: boolean;
    noOpType: GDFWmoVolcanoCloudNoOpType;
    clouds: GDFWmoVolcano2CloudRecord[];
}

export enum GDFProductType {
    standard = 0x00000001, // Standard vector overlay
    geolay = 0x00000002, // Raster based overlay
    popup = 0x00000003
}

export enum GDFWmoStormType {
    depression,
    tropicalDepression,
    tropicalStorm,
    hurricane,
    typhoon,
    sandStorm
}

export enum GDFRecordType {
    title = 0, // Hdr
    datetime = 1, // Hdr
    expires = 2, // Hdr
    class = 3,
    propset = 4,
    text = 5,
    poly = 6,
    point = 7,
    rect = 8,
    elipse = 9,
    legend = 10, // Hdr
    ccfp = 11,
    sua = 12,
    hotpoly = 13,
    datetime2 = 14,
    lightning = 15,
    radsum = 16,
    delay = 17,
    metar = 18,
    taf = 19,
    rvr = 20,
    diversion = 21,
    airportload = 22,
    airspaceload = 23,
    fca = 24,
    tfmdireroute = 25,
    front = 26,
    prodlink = 27,
    label = 28,
    winds = 29,
    pirep = 30,
    rangering = 31,
    custom = 32, // custom draw
    pirep2 = 33,
    dynroute = 34,
    status = 50, // Hdr
    navinfo = 51, // Hdr
    rle1 = 52,
    navinfo2 = 53, // Hdr
    frames = 54,
    nomad = 60, // Temporary object for NOMAD
    wmotrop = 70, // WMO Tropopause
    wmocloud = 71, // WMO Cloud
    wmovolcano = 72, // WMO Volcano
    wmojetstream = 73, // WMO JetStream
    atsas = 74, // ATSAS lightning for Australia
    hotpolyhi = 75, // new hotpoly type for hi res points
    windssm = 76, // winds compact
    wmocat = 77, // WMO Clear Air Turbulence
    wmostorm = 78, // WMO Storm (tropic/sand)
    wmohzdvol = 79, // WMO Hazard, Volcano
    wmohzdrad = 80, // WMO Hazard, Radiation
    taps = 81, // WSI TAPS (public)
    iataturb = 82, // IATA Turbulence reports
    windscompact = 83, // CAE Winds compact format (one grid description, multiple uv data records)
    wmovolcano2 = 84,
    //
    eof = 255 // No data follows, so no structure is
}

export enum GDFSubRecordType {
    wmoAshCloud,
    wmoAshCloud2
}

// Fe palette codes start from 0x00000fff.
export enum GDFPalletCode {
    none = 0x00000000, // Class defined colors only (by object type)
    nexrad16 = 0x00000008, // 16 color NEXRAD palette
    nexrad48 = 0x00000010, // 48 color NEXRAD palette
    precip = 0x00000018,
    satellite = 0x00000020, // 30 level satellite palette
    radsum = 0x00000028, // Radar summary palette
    airmets = 0x00000030, // Airmet palette
    sigmets = 0x00000038, // Sigmet palette
    csigmets = 0x00000040, // Convective Sigmet palette
    watchwarn = 0x00000048, // Watch/waring palette
    tfr = 0x00000050, // TFR palette
    echotops = 0x00000058, // NEXRAD echo tops
    radcan = 0x00000060, // Canadian radar (uses standard NEXRAD 16 levels; see other palette below for real Canada palette)
    satvis = 0x00000068, // Visible satellite palette (64 levels)
    satir = 0x00000070, // IR satellite palette (64 levels)
    satwv = 0x00000078, // Water vapor satellite palette (64 levels)
    ccfp = 0x00000080, // CCFP
    lightning = 0x00000088, // Lightning (30-minute delay)
    lightning0 = 0x00000090, // Lightning (no delay)
    delay = 0x00000098, // FAA delays
    metar = 0x000000a0, // METAR
    rvr = 0x000000a8, // RVR
    diversion = 0x000000b0, // Diversions
    icing = 0x000000b8, // Icing
    turbllca = 0x000000c0, // Turbulence (low level and clear air)
    turbconv = 0x000000c8, // Turbulence (convective)
    winds = 0x000000d0, // Winds
    tempalft = 0x000000d8, // Temps (aloft)
    tempsfc = 0x000000e0, // Temps (surface)
    fca = 0x000000e8, // FCA
    afp = 0x000000f0, // AFP
    satvis_256 = 0x000000f8, // Satellite visible (only 140 elements used)
    satir_256 = 0x00000100, // IR satellite palette 256 color (altitude remapped; 500 ft increments)
    lamp_prb = 0x00000108, // LAMP Thunderstorm probibility colors (0-100)
    lamp_cat = 0x00000110, // LAMP Thunderstorm Categorical colors (0-1)
    ncwf = 0x00000118, // NCWF colors (0-6)
    nexrad64 = 0x00000120, // 64 color NEXRAD palette (rain, mixed, freezing rain, snow)
    radarcan = 0x00000128, // 10 color Canadian radar palette (not correlated iwth US 16-level colors)
    radarcov = 0x00000130, // 1 color coverage ring color
    satir_256t = 0x00000138, // IR satellite palette 256 color (temperature based)
    satvis_256f = 0x00000140, // Visible satellite palette, full 256 colors
    lightning1 = 0x00000148, // Lightning (1 minute, 0-15 or 0-30 history; no delay)
    echotops70 = 0x00000150, // Raster echo tops with 70 data levels (and two invalid/no data levels; 0-71)
    satwv_256f = 0x00000158, // Water vapor satellite palette, full 256 colors
    sehzd_turb = 0x00000160, // DTN Turbulunce Hazard
    sehzd_icing = 0x00000168, // DTN Icing Hazard
    sehzd_tstorm = 0x00000170, // DTN TStorm Hazard
    ozone = 0x00000178, // DTN TStorm Hazard
    satwv_256 = 0x00000180, // Water vapor satellite palette 256 colors
    gtg = 0x00000188, // GTG (Graphical Turbulence Guidance)
    hiwc = 0x00000190, // HIWC (High Ice Water Content) raster
    turbadv = 0x00000198, // WSI Turbulence Advisory
    tafs = 0x000001a0, // WSI TAPS product
    dsua = 0x000001a8, // SFE Dynamic SUA product
    turb_cat = 0x000001b0, // CAE turbulence CAT
    icing_new = 0x000001b8, // CAE icing
    turb_incloud = 0x000001c0, // CAE turbulence InCloud
    turb_edr = 0x000001c8, // CAE EDR turbulence
    cb_tops = 0x000001d0, // CAE cumulonimbus tops
    cb_extents = 0x000001d8, // CAE cumulonimbus extents
    tropical = 0x000001e0, // Tropical storm products
    volcash = 0x000001e8, // Volcanic ash products
    iataturb = 0x000001f0, // IATA turbulence reports
    vec_hiwc = 0x000001f8, // High Ice Water Content (Vector)  // 16.5
    opiturb = 0x00000200, // OPI turbulence
    lightning_prb = 0x00000208,
    lightning_density = 0x00000210,
    lightning_jumpgrid = 0x00000218,
    contrail = 0x00000220, // Contrail
    global_ref = 0x00000228, // GFS global reflectivity
    satir_256n = 0x00000230, // DTN IR sat altitude based
    wsi_fpg = 0x00000238,
    probsevere = 0x00000240,
    centerWeatherAdvisory = 0x00000248,
    // Front only
    temp_heatmap = 0x00000fff, // Temperature heatmap
    wind_heatmap = 0x00001000, // Wind heatmap,
    iata_turb_severity_industry_white = 0x00001001,
    iata_turb_severity_industry_gray = 0x00001002,
    iata_turb_severity_iata_white = 0x00001003,
    iata_turb_severity_iata_gray = 0x00001004,
    iata_turb_edr_gray = 0x1005,
    iata_turb_edr_white = 0x1006,
    metar_ceiling = 0x1007, // METAR ceiling
    metar_visibility = 0x1008, // METAR visibility,
    lightning0_faded,
    lightning0_discrete,
    lightning1_faded,
    lightning1_discrete,
    taps,
    volcash_light,
    volcash_dark,
    probsevere_light,
    probsevere_dark,
    volcash_cloud2
}

export enum GDFHotPollyShape {
    unknown = 0,
    point = 1,
    line = 2,
    /** @deprecated */
    ellipse = 3,
    /** @deprecated */
    rect = 4,
    polygon = 5,
    radius = 6 // 1st point = center, 2nd point iLat = radius * 100
}

export enum GDFFcaShape {
    unknown = 0,
    poly = 1,
    line = 2,
    circle = 3,
    airport = 4,
    sector = 5,
    base = 6,
    tracon = 7,
    baseTracon = 8,
    artcc = 9,
    fix = 10,
    sua = 11
}

export enum GDFHotPollyTypes {
    airmet_turb_sfc = 1,
    airmet_turb_aloft = 2,
    airmet_icing = 3,
    airmet_ifr = 4,
    airmet_mtnobsc = 5,
    airmet_wind_sfc = 6,
    sigmet_turb_sfc = 7,
    sigmet_turb_aloft = 8,
    sigmet_icing = 9,
    sigmet_visibility = 10,
    sigmet_conv = 11,
    watch_tstorm = 12,
    watch_tornado = 13,
    notam_tfr = 14,
    sigmet_volcash = 15,
    sigmet_blowdust = 16,
    hazard_volcash = 17,
    hazard_blowdust = 18,
    hazard_convective = 19,
    hazard_icing = 20,
    hazard_turb_aloft = 21,
    nexrad = 22,
    precip = 23,
    turbulence = 24,
    icing = 25,
    jetstream = 26,
    trop_height = 27,
    trop_low = 28,
    tropical_cyclone = 29,
    volcano = 30,
    tstorm = 31,
    route = 32,
    weather_front = 33,
    pressure_high = 34,
    pressure_low = 35,
    turbulence_lowlevel_adv = 36,
    turbulence_lowlevel_obv = 37,
    turbulence_clearair_adv = 38,
    turbulence_clearair_obv = 39,
    turbulence_convective_adv = 40,
    turbulence_convective_obv = 41,
    turbulence_alert_adv = 42,
    turbulence_alert_obv = 43,
    turbulence_advisory_adv = 44,
    turbulence_advisory_obv = 45,
    warning_tstorm = 46,
    warning_tornado = 47,
    sigmet_blowsand = 48,
    sigmet_tropcyclone = 49,
    sigmet_tstorm = 50,
    ncwf_tstorm = 51,
    raim = 52,
    sigmet_mtnobsc = 53,
    sigmet_mtnwave = 54,
    sigmet_llws = 55,
    freeze_level = 56,
    hazard_ozone_wsi = 57, // WSI single Ozone level
    sehzd_turbulence = 58, // DTN Enhance Turbulunce Hazard
    sehzd_icing = 59, // DTN Enhance Icing Hazard
    sehzd_tstorm = 60, // DTN Enhance Thunderstorm Hazard
    ozone = 61, // DTN Ozone (and future FE products)
    hazard_turb_advisory = 62, // WSI Turbulence Advisory
    sua = 63, // SFE Dynamic SUA
    wind_field = 64, // Tropical storms wind field
    turbulence_cat = 65, // Clear Air Turbulence
    icing_new = 66, // Icing
    turbulence_incloud = 67, // In Cloud Turbulence
    turbulence_edr = 68, // EDR Turbulence
    cumulonimbus_tops = 69, // Cumulonimbus tops
    cumulonimbus_extents = 70, // Cumulonimbus extents
    tropical_storm_path = 71, // Tropical storm path
    hiwc = 72, // High Ice Water Content, 16.5
    turbulence_gtgnowcast = 73, // GTGNowcast EDR Turbulence, 16.5
    tropical_stomr_path = 74,
    mrms_ltng_prob = 75, // MRMS Lightning Probability
    mrms_ltng_jump = 76, // MRMS Lightning Jump Rapid
    contrail = 77, // Contrail
    storm_track = 78, // Storm track path
    probsevere = 79, // ProbSevere polygons
    airmet_llws = 80, // Airmet Low Level Winds Shear
    airmet_freezing = 81, // Airmet freezing
    sig_icing = 82,
    sig_tropopause = 83,
    cwa_tstorm = 84,
    cwa_turb = 85,
    cwa_icing = 86,
    cwa_ifr = 87,
    cwa_precipitation = 88,
    cwa_unknown = 89
}

export enum GDFMetarTafFlightCategory {
    unknown = 0,
    vfr = 1,
    mvfr = 2,
    ifr = 3,
    lifr = 4,
    bcat1 = 5
}

export enum GDFCcfpGeometryType {
    area = 0,
    line = 1
}

export enum GDFCustomDrawShape {
    point = 0,
    line = 1,
    polygon = 2,
    ellipse = 3
}

export enum GDFCustomDrawPenStyle {
    solid = 0,
    dash = 1,
    dot = 2,
    dashdot = 3,
    dastdotdot = 4
}

export enum GDFCustomDrawFillStyle {
    solid = 0,
    horizontal,
    verstical,
    forwardDiagonal,
    backwardDiagonal
}

export enum GDFCustomDrawCap {
    unused,
    square,
    round,
    arrow
}

export enum GDFCustomDrawEllipseType {
    unkonwn,
    trueShape,
    trueDistance,
    pixelAccurate
}

export enum GDFFcaType {
    fca = 0,
    fea = 1
}
export enum GDFWmoTropType {
    unknown = 0,
    spot = 1,
    high = 2,
    low = 3
}

export enum GDFFrontType {
    undefined = 0,
    highCenter = 1,
    lowCenter = 2,
    cold = 3,
    warm = 4,
    stationary = 5,
    occluded = 6,
    surfaceTrough = 7,
    squallLine = 8,
    dryline = 9,
    tropicalWave = 10,
    stationaryCW = 11,
    stationaryWC = 12,
    stationaryWW = 13,
    stationaryCC = 14,
    isobarPressureLine = 15
}

export enum GDFTapsType {
    noTurb = 0,
    timeAvgTurb = 1,
    heartbeat = 2
}

export enum GDFWmoVolcanoCloudNoOpType {
    null = 0,
    test = 1,
    exercise = 2
}
